import { FC } from "react";
import { ReactKeycloakProvider, useKeycloak } from "@react-keycloak/web";
import { UnauthenticatedPlaceholder } from "../components/layout/UnauthenticatedPlaceholder";
import { AuthenticatedLayout, NavigationDefinition } from "../components/layout/AuthenticatedLayout";
import Keycloak from "keycloak-js";
import ConfigInterface from "../ConfigInterface";
import { useApiClient } from "../api";
import { hasAnyPermission, PermissionsResolver, usePermissions } from "./permissions";
import { SplashScreen } from "../components/layout/SplashScreen";

export const AuthProvider: FC<{ children: React.ReactNode; authClient: Keycloak }> = ({ children, authClient }) => {
  return (
    <ReactKeycloakProvider
      authClient={authClient}
      initOptions={{ onLoad: "login-required" }}
      onEvent={(event, error) => {
        console.log("KeycloakEvent", { event, error });
        if (error) {
          console.error("AUTH: ", error);
        } else {
          console.debug("AUTH: ", event);
        }
      }}
    >
      {children}
    </ReactKeycloakProvider>
  );
};
export const AuthMiddleware: FC<{
  children: React.ReactNode;
  config: ConfigInterface;
  client: keyof ReturnType<typeof useApiClient>;
  topChildren?: React.ReactNode;
  navigation: NavigationDefinition;
  /**
   * Keycloak realm roles that may use the application. Use `"any-capability"` to instead allow every user whose
   * capabilities response grants at least one permission, so roles created by a tenant can log in too.
   */
  allowedRoles: string[] | "any-capability";
  appTitle: string;
  appSubTitle: string;
  image: string | null;
}> = (props) => {
  const { keycloak } = useKeycloak();
  const client = useApiClient(props.config)[props.client];
  if (!keycloak.authenticated) {
    return <UnauthenticatedPlaceholder />;
  }
  const layout = (
    <AuthenticatedLayout
      navigation={props.navigation}
      client={client}
      title={props.appTitle}
      subtitle={props.appSubTitle}
      image={props.image}
      topChildren={props.topChildren}
    >
      {props.children}
    </AuthenticatedLayout>
  );
  if (props.allowedRoles === "any-capability") {
    return <CapabilitiesGate client={client}>{layout}</CapabilitiesGate>;
  }
  const roles = keycloak?.realmAccess?.roles ?? [];
  if (!props.allowedRoles.some((role) => roles.includes(role))) {
    return <UnauthenticatedPlaceholder reason={"Je hebt geen toegang tot deze applicatie"} />;
  }
  return layout;
};

const CapabilitiesGate: FC<{ children: React.ReactNode; client: PermissionsResolver }> = ({ children, client }) => {
  const { permissions, isLoading } = usePermissions(client);
  if (isLoading) {
    return <SplashScreen />;
  }
  if (!hasAnyPermission(permissions)) {
    return <UnauthenticatedPlaceholder reason={"Je hebt geen toegang tot deze applicatie"} />;
  }
  return <>{children}</>;
};
